import { ExcelProcessorUtils } from '@/utils/ExcelProcessorUtils';
import { buildNewTaskLock } from '@/utils/TaskLockUtils';
import { useTaskStore } from '@/stores/task';

export class TaskSetProfileProcessor {
    constructor(selectedSchedule) {
      this.taskStore = useTaskStore();
      this.selectedSchedule = selectedSchedule;
      this.excelUtils = new ExcelProcessorUtils(selectedSchedule);
      this.taskSetsToGenerate = {};
      this.taskSetsPayload = {};
    }

    processTaskSetProfileForTask(taskObject, taskSetValue, existingTask = null, taskSetMode = 'all') {
      const taskSets = this.taskStore.getTaskSetProfiles || [];
      const matchingTaskSetProfile = taskSets.find(ts =>
        ts.title?.toString().trim().toLowerCase() === taskSetValue.toLowerCase()
      );

      if (!matchingTaskSetProfile) return;

      if (this.isTaskSetAlreadyDeployed(existingTask, matchingTaskSetProfile, taskSetMode)) {
        return;
      }

      this.processTaskSetProfile(matchingTaskSetProfile, taskObject);
    }

    isTaskSetAlreadyDeployed(existingTask, taskSetProfile, taskSetMode) {
      if (!existingTask) return false;

      const profileId = taskSetProfile.profile?.id;
      if (!profileId) return false;

      const ownDeployedId = existingTask.meta?.selectedTaskSetProfileId
                            || existingTask.meta?.taskSetProfileMeta?.taskSetProfileId;
      if (ownDeployedId === profileId) return true;

      if (taskSetMode === 'parentOnly' && existingTask.children) {
        return existingTask.children.some(child =>
          child.meta?.taskSetProfileMeta?.taskSetProfileId === profileId
        );
      }

      return false;
    }
  
    processTaskSetProfile(taskSetProfile, task) {
      if (this.taskSetsToGenerate[taskSetProfile.code]) {
        this.taskSetsToGenerate[taskSetProfile.code].tasks.push(task);
        this.taskSetsPayload[taskSetProfile.profile.id].deployments.push({
          id: '',
          parentTaskId: task.id,
        });
      } else {
        this.taskSetsToGenerate[taskSetProfile.code] = {
          taskSetProfile,
          tasks: [task]
        };
        this.taskSetsPayload[taskSetProfile.profile.id] = {
          deployments: [{
            id: '',
            parentTaskId: task.id
          }]
        };
      }
    }
  
  
    /**
     * Preview-only mirror of the API's deployment seeding
     * (CalendarCalculationService.SeedDeploymentTaskDates): a deployed task's
     * tracking time range starts where the parent's range for the same template
     * starts, falling back to now when the parent has no date. The generated
     * tasks never reach the commit payload (the server creates them from
     * taskSetsPayload), but they are validated and drawn in the preview, so
     * without a start date every deployment tripped the missing-start-date check.
     * 'none' ranges have no scheduling semantics and stay undated.
     */
    seedTimeRangesFromParent(parentTask, now = new Date()) {
      const fallbackStart = now.toISOString();
      return this.excelUtils.getTimeRanges(null).map(timeRange => {
        if (timeRange.timeRangeTemplateType === 'none') return timeRange;
        const anchor = (parentTask?.timeRanges || []).find(
          tr => tr.timeRangeTemplateId === timeRange.timeRangeTemplateId
        );
        return { ...timeRange, startDate: anchor?.startDate || fallbackStart };
      });
    }

    generateTasksFromTaskSetProfile(tasksetProfile, parentTask) {
      if (!tasksetProfile) return [];
  
      const generatedTasks = [];
      const baseWBS = parentTask ? parentTask.wbs.map(String) : [];
      // One anchor for the whole deployment: the server seeds every deployed
      // task, nested ones included, from the deployment parent.
      const seedNow = new Date();
      let counter = 1;
  
      tasksetProfile.taskProfiles.forEach(taskProfile => {
        const currentWBS = [...baseWBS, counter.toString()];
        const taskId = this.excelUtils.generateNewId();
        
        const task = {
          id: taskId,
          code: taskProfile.code || counter.toString(),
          wbs: currentWBS,
          title: taskProfile.title,
          parentTaskId: parentTask ? parentTask.id : '',
          childTaskIds: [],
          subtasks: [],
          prerequisiteTaskIds: [],
          scheduleId: this.selectedSchedule.id,
          viewableLocationIds: parentTask ? [...parentTask.viewableLocationIds] : [],
          meta: {},
          key: "",
          lock: buildNewTaskLock(this.excelUtils.getTimeRangeTemplateIds()),
          labels: [],
          sort: counter,
          children: [],
          timeRanges: this.seedTimeRangesFromParent(parentTask, seedNow),
          tags: []
        };
  
        if (taskProfile.subtaskProfiles && taskProfile.subtaskProfiles.length > 0) {
          taskProfile.subtaskProfiles.forEach(subtaskProfile => {
            const subtask = this.excelUtils.createSubtask({
              title: subtaskProfile.title,
              type: subtaskProfile.itemType,
              code: subtaskProfile.itemTemplateCode
            });
            task.subtasks.push(subtask);
          });
        }
  
        generatedTasks.push(task);
  
        if (taskProfile.children && taskProfile.children.length > 0) {
          let childCounter = 1;
          taskProfile.children.forEach(childProfile => {
            const childWBS = [...currentWBS, childCounter.toString()];
            const childTask = {
              id: this.excelUtils.generateNewId(),
              code: childProfile.code || childCounter.toString(),
              wbs: childWBS,
              title: childProfile.title,
              parentTaskId: taskId,
              childTaskIds: [],
              subtasks: [],
              prerequisiteTaskIds: [],
              scheduleId: this.selectedSchedule.id,
              viewableLocationIds: [...task.viewableLocationIds],
              meta: {},
              key: "",
              lock: buildNewTaskLock(this.excelUtils.getTimeRangeTemplateIds()),
              labels: [],
              sort: childCounter,
              children: [],
              timeRanges: this.seedTimeRangesFromParent(parentTask, seedNow),
              tags: []
            };
  
            if (childProfile.subtaskProfiles && childProfile.subtaskProfiles.length > 0) {
              childProfile.subtaskProfiles.forEach(subtaskProfile => {
                const subtask = this.excelUtils.createSubtask({
                  title: subtaskProfile.title,
                  type: subtaskProfile.itemType,
                  code: subtaskProfile.itemTemplateCode
                });
                childTask.subtasks.push(subtask);
              });
            }
  
            task.childTaskIds.push(childTask.id);
            task.children.push(childTask);
            generatedTasks.push(childTask);
            childCounter++;
          });
        }
  
        counter++;
      });
  
      return generatedTasks;
    }
  
    getTaskSetsToGenerate() {
      return this.taskSetsToGenerate;
    }
  
    getTaskSetsPayload() {
      return this.taskSetsPayload;
    }
  }